/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useState, useRef, type ReactElement } from 'react';
import { Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { RadioGroup, RadioGroupItem } from '@/components/forms/radio-group';
import { Label } from '@/components/forms/label';
import { Button } from '@/components/buttons';
import Badge from '@/components/badge';
import { Typography } from '@/components/typography';
import { cn } from '@/lib/utils';
import { formatAddress } from '@/lib/address/address-utils';
import { formatPhoneCompact } from '@/lib/address/phone-utils';
import type { AddressBookItem } from '@/lib/customer/profile-utils';

const DEFAULT_MAX_VISIBLE = 3;

export type SavedAddressesListProps = {
    /** List of saved addresses to display */
    addresses: AddressBookItem[];
    /** Max number of addresses shown before "View All" (default 3) */
    maxVisible?: number;
    /** Currently selected address id (controlled) */
    value?: string;
    /** Callback when selection changes */
    onValueChange?: (value: string) => void;
    /**
     * Called when "Add New Address" is clicked. Kept for legacy callers — the
     * preferred UX is to render the Add button in the parent's card header (per
     * Figma). Pass this prop and the button will be rendered above the grid.
     */
    onAddNewAddress?: () => void;
    /** Called when "Edit Address" is clicked for a specific address */
    onEditAddress?: (addressId: string) => void;
};

/**
 * Displays the customer's saved shipping addresses as selectable cards in the
 * Shipping Address checkout stage.
 *
 * Layout (per Figma):
 *   - Responsive 3-column grid (3 desktop / 2 tablet / 1 mobile) with up to
 *     `maxVisible` cards visible and a "View All" toggle to expand.
 *   - The preferred (default) card has a "DEFAULT" badge in the top-left of the
 *     header row, with the customer name moved down into the body (above the
 *     address lines). Non-default cards put the customer name *into* the
 *     header row, alongside the EDIT button on the right. This keeps the EDIT
 *     button right-anchored on every card and lets the body content (address +
 *     phone) reflow naturally without wrapping under the button.
 *   - The "EDIT" affordance is a compact outlined button (pencil icon + label),
 *     anchored top-right of every card. `editAddressLink` supplies its
 *     accessible label (includes the customer name for screen-reader context),
 *     while the visible text is the shorter `common.edit` per design.
 *   - Phone is rendered in the compact `+1 888 123-4567` form via
 *     `formatPhoneCompact` (no parentheses), matching the Figma.
 *   - Selection is indicated solely by a thicker border on the selected card.
 *     The radio input is `sr-only` (kept for keyboard + screen-reader users)
 *     and the wrapping <Label> makes the whole card clickable.
 */
export function SavedAddressesList({
    addresses,
    maxVisible = DEFAULT_MAX_VISIBLE,
    value,
    onValueChange,
    onAddNewAddress,
    onEditAddress,
}: SavedAddressesListProps): ReactElement {
    const { t } = useTranslation('checkout');
    const [showAll, setShowAll] = useState(false);
    const radioGroupRef = useRef<HTMLDivElement>(null);

    if (addresses.length === 0) {
        return <></>;
    }

    const defaultSelected = value ?? addresses.find((a) => a.preferred)?.id ?? addresses[0]?.id ?? '';
    const selectedId = value ?? defaultSelected;
    const visibleAddresses = showAll ? addresses : addresses.slice(0, maxVisible);
    const hasMore = addresses.length > maxVisible;
    const moreCount = hasMore ? addresses.length - maxVisible : 0;

    const handleViewLess = () => {
        setShowAll(false);
        // Scroll to center the address list in viewport after collapsing.
        setTimeout(() => {
            if (radioGroupRef.current) {
                const element = radioGroupRef.current;
                const elementRect = element.getBoundingClientRect();
                const elementTop = elementRect.top + window.scrollY;
                const elementHeight = elementRect.height;
                const viewportHeight = window.innerHeight;
                const offsetPosition = elementTop - viewportHeight / 2 + elementHeight / 2;
                window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
            }
        }, 50);
    };

    return (
        <div className="space-y-4">
            {onAddNewAddress && (
                <div className="flex justify-end">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={onAddNewAddress}
                        aria-label={t('shippingAddress.newAddressButton')}>
                        {t('shippingAddress.newAddressButton')}
                    </Button>
                </div>
            )}
            <div ref={radioGroupRef}>
                <RadioGroup
                    value={selectedId}
                    onValueChange={onValueChange}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
                    aria-label={t('shippingAddress.selectSavedAddress')}>
                    {visibleAddresses.map((addr) => {
                        const isSelected = selectedId === addr.id;
                        const isDefault = !!addr.preferred;
                        const { nameLine, streetLine, cityLine } = formatAddress(addr);
                        const editAriaLabel = nameLine
                            ? `${t('shippingAddress.editAddressLink')} — ${nameLine}`
                            : t('shippingAddress.editAddressLink');
                        return (
                            <Label
                                key={addr.id}
                                htmlFor={`saved-address-${addr.id}`}
                                className={cn(
                                    // Card frame — mirrors Figma node 35528:58481 (Shipping card):
                                    // horizontal flex with the content column on the left and the
                                    // EDIT button on the right; selected vs non-selected differ only
                                    // in border color (both 1px). Padding tuned to p-4 (16px) —
                                    // tighter than the Figma's p-5 because our 3-column grid sizes
                                    // each card narrower than the Figma's fixed 265px frame, and
                                    // p-5 + extra row stretching produced visible empty space at
                                    // the bottom of cards with fewer address lines.
                                    //
                                    // Focus ring uses `has-[:focus-visible]:` (not `focus-within:`)
                                    // so the ring only appears when the sr-only radio receives
                                    // KEYBOARD focus — clicking the card with a mouse picks up the
                                    // selection border but does NOT add a ring on top (avoids the
                                    // "double highlight" where ring + border both render after a
                                    // click leaves the radio focused).
                                    'relative flex items-start justify-between gap-2 p-4 border cursor-pointer transition-colors min-w-0 bg-card has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2',
                                    isSelected ? 'border-foreground' : 'border-border hover:border-muted-foreground'
                                )}
                                data-testid={`saved-address-card-${addr.id}`}>
                                {/* Radio input — visually hidden, kept for a11y. The wrapping
                                    Label exposes the whole card as the click target and supplies
                                    the radio's accessible name (the address body).

                                    The `!` (important) overrides are required because the shared
                                    RadioGroupItem primitive hardcodes `size-4 border` for its
                                    visible dot, and tailwind-merge doesn't class `sr-only` as a
                                    conflict with `size-*`, so the primitive's 16×16 box would
                                    otherwise survive `sr-only` and push the card content right. */}
                                <RadioGroupItem
                                    value={addr.id}
                                    id={`saved-address-${addr.id}`}
                                    className="sr-only !size-0 !border-0 !m-0 !p-0"
                                />
                                {/* Content column — Badges (default only) above Content block.
                                    Inner gaps: gap-2 (8px) between the Badges row and the
                                    Content block — tighter than the Figma's 16px because our
                                    cards are narrower and the wider gap left visible dead
                                    space. Inside Content the lines (name / address / phone)
                                    stay at the Figma's gap-1.5 (6px). */}
                                <div className="flex-1 min-w-0 flex flex-col gap-2">
                                    {isDefault && (
                                        <div className="flex items-center gap-1.5">
                                            <Badge
                                                variant="secondary"
                                                className="rounded-sm bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-widest px-2 py-0.5">
                                                {t('shippingAddress.defaultBadge')}
                                            </Badge>
                                        </div>
                                    )}
                                    <div className="flex flex-col gap-1.5">
                                        {nameLine && (
                                            <Typography
                                                variant="small"
                                                className="text-sm font-semibold leading-5 text-foreground">
                                                {nameLine}
                                            </Typography>
                                        )}
                                        {(streetLine || cityLine) && (
                                            <div className="text-sm leading-5 text-muted-foreground">
                                                {streetLine && <p>{streetLine}</p>}
                                                {cityLine && <p>{cityLine}</p>}
                                            </div>
                                        )}
                                        {addr.phone && (
                                            <Typography
                                                variant="small"
                                                className="text-sm leading-5 text-muted-foreground">
                                                {formatPhoneCompact(addr.phone)}
                                            </Typography>
                                        )}
                                    </div>
                                </div>
                                {/* EDIT button — sibling of the content column, anchored top-right
                                    of the card. Outline + pencil + "Edit" label per Figma. */}
                                {onEditAddress && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="h-7 shrink-0 px-2 gap-1 text-xs font-semibold leading-4 tracking-button"
                                        onClick={(e) => {
                                            // Stop the Label from also toggling the radio.
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onEditAddress(addr.id);
                                        }}
                                        aria-label={editAriaLabel}>
                                        <Pencil className="size-3.5" aria-hidden />
                                        <span>{t('common.edit')}</span>
                                    </Button>
                                )}
                            </Label>
                        );
                    })}
                </RadioGroup>
            </div>
            {hasMore && (
                <div>
                    {!showAll ? (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-sm font-medium text-foreground"
                            onClick={() => setShowAll(true)}
                            aria-expanded={false}
                            aria-label={t('shippingAddress.viewAllLink')}>
                            {t('shippingAddress.viewAllLink')} {t('shippingAddress.viewAllMore', { count: moreCount })}
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-sm font-medium text-foreground"
                            onClick={handleViewLess}
                            aria-expanded={true}
                            aria-label={t('shippingAddress.viewLessAddresses')}>
                            {t('shippingAddress.viewLessAddresses')}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}

export default SavedAddressesList;
